import React from 'react';
import './wes-modal.scss';
import PropTypes from 'prop-types';
import {
  AlertCircle, AlertTriangle, CheckCircle, Info,
} from 'react-feather';
import clsx from 'clsx';
import { ButtonTypes } from '../button';
import { FeaturedIconColors } from '../featured-icon';
import Modal from '../modal';
import Icon from '../icon';
import useTranslation from '../../../utils/translation-utils';
import useMobileView from '../../enhancers/custom-hook/use-mobile-view';

const WesModalTypes = {
  SUCCESS: 'wmd-success',
  WARNING: 'wmd-warning',
  DESTRUCTIVE: 'wmd-destructive',
  PRIMARY: 'wmd-primary',
};

// WES stands for Warning, Error and Success
const WesModal = ({
  open, type, header, description, onConfirm, onCancel, className, showClose,
  renderIcon, buttonConfig,
  onClose,
}) => {
  const [getTranslation] = useTranslation();
  const isMobileView = useMobileView();

  const ButtonConfiguration = {
    [WesModalTypes.PRIMARY]: [
      {
        id: 'cancel',
        type: ButtonTypes.SECONDARY,
        label: getTranslation('CANCEL'),
      }, {
        id: 'confirm',
        type: ButtonTypes.PRIMARY,
        label: getTranslation('CONFIRM'),
      },
    ],
    [WesModalTypes.SUCCESS]: [
      {
        id: 'cancel',
        type: ButtonTypes.SECONDARY,
        label: getTranslation('CANCEL'),
      }, {
        id: 'confirm',
        type: ButtonTypes.PRIMARY,
        label: getTranslation('CONFIRM'),
      },
    ],
    [WesModalTypes.WARNING]: [
      {
        id: 'cancel',
        type: ButtonTypes.SECONDARY,
        label: getTranslation('CANCEL'),
      }, {
        id: 'confirm',
        type: ButtonTypes.PRIMARY,
        label: getTranslation('CONFIRM'),
      },
    ],
    [WesModalTypes.DESTRUCTIVE]: [
      {
        id: 'cancel',
        type: ButtonTypes.SECONDARY,
        label: getTranslation('CANCEL'),
      }, {
        id: 'confirm',
        type: ButtonTypes.DESTRUCTIVE,
        label: getTranslation('DELETE'),
      },
    ],
  };

  const handleButtonClick = (id) => {
    switch (id) {
      case 'cancel':
        if (onCancel && typeof onCancel === 'function') {
          onCancel.call(null);
        }
        break;
      case 'confirm':
        if (onConfirm && typeof onConfirm === 'function') {
          onConfirm.call(null);
        }
        break;
      default:
        break;
    }
  };

  const getIconType = () => {
    switch (type) {
      case WesModalTypes.DESTRUCTIVE:
        return AlertCircle;
      case WesModalTypes.WARNING:
        return AlertTriangle;
      case WesModalTypes.PRIMARY:
        return Info;
      case WesModalTypes.SUCCESS:
      default:
        return CheckCircle;
    }
  };

  const getIconColor = () => {
    switch (type) {
      case WesModalTypes.DESTRUCTIVE:
        return FeaturedIconColors.ERROR;
      case WesModalTypes.WARNING:
        return FeaturedIconColors.WARNING;
      case WesModalTypes.PRIMARY:
        return FeaturedIconColors.PRIMARY;
      case WesModalTypes.SUCCESS:
      default:
        return FeaturedIconColors.SUCCESS;
    }
  };

  const onWesModalClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose.call(null);
    } else {
      onCancel();
    }
  };

  const renderIconUsingIconType = () => <Icon icon={getIconType()} color={getIconColor()} />;

  const updatedRenderIcon = renderIcon || renderIconUsingIconType;

  return (
    <Modal
      open={open}
      className={clsx(type, className)}
      renderIcon={!isMobileView ? updatedRenderIcon : null}
      iconColor={getIconColor()}
      header={header}
      description={description}
      buttons={buttonConfig || ButtonConfiguration[type]}
      onButtonClick={handleButtonClick}
      showClose={showClose}
      onClose={onWesModalClose}
    />
  );
};

WesModal.propTypes = {
  open: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(WesModalTypes)),
  header: PropTypes.string,
  description: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  showClose: PropTypes.bool,
  className: PropTypes.string,
  renderIcon: PropTypes.func,
  buttonConfig: PropTypes.arrayOf(PropTypes.shape({})),
};

WesModal.defaultProps = {
  open: false,
  type: WesModalTypes.SUCCESS,
  header: '',
  renderIcon: null,
  description: '',
  onConfirm: () => { },
  onCancel: () => { },
  onClose: () => { },
  showClose: false,
  className: '',
  buttonConfig: null,
};

export {
  WesModal as default,
  WesModalTypes,
};
